import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2, Pencil, Trash2 } from 'lucide-react'
import { useDeleteProfile, useProfiles, useSaveProfile } from '@/hooks/useInventoryMaster'
import { useStorageConditions, useStorageInvalidator, useStorageRoots } from '@/hooks/useStorage'
import { profileDisplayName } from '@/lib/keptAt'
import { usePermission } from '@/lib/permissions'
import { keptAtOptionFields, keptAtOptionRows, optionNameTaken, type KeptAtOptionRow } from '@/lib/storageTemperatures'
import type { StorageConditionMeta } from '@/types/storage'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { KeptAtBadge } from '../KeptAtBadge'
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog'

type Draft = { name: string; condition: string }

/**
 * Kept at options tab: the names a unit's "Kept at" menu picks from, each
 * pointing at one temperature. Behind the scenes these are the inventory
 * storage profiles — the same ones a material points at for what it requires —
 * but storage UI never calls them that.
 *
 * Only an option on a temperature that can be used for storage AND is active
 * is offered in the menu; the rest are listed with the reason. "＋ Add option"
 * works for any such temperature, whether it has no option yet or already has
 * one (e.g. "Freezer A" and "Freezer B", both −80 °C).
 */
export function KeptAtOptionsTab() {
  const { can } = usePermission()
  const canEdit = can('inventory.configure')
  const profiles = useProfiles('storage')
  const reference = useStorageConditions()
  const roots = useStorageRoots()
  const save = useSaveProfile()
  const remove = useDeleteProfile()
  const queryClient = useQueryClient()
  const invalidateStorage = useStorageInvalidator()
  // Saving or deleting an option re-points the units and locations on it
  // server side; the profile hooks refresh only profiles (and materials).
  const refreshStorage = () => {
    void invalidateStorage()
    void queryClient.invalidateQueries({ queryKey: ['inventory', 'materials'] })
  }

  const [adding, setAdding] = useState<Draft | null>(null)
  /** The option being edited in place, by profile id. One at a time. */
  const [editing, setEditing] = useState<{ id: string; draft: Draft } | null>(null)
  const [deleting, setDeleting] = useState<KeptAtOptionRow | null>(null)
  const [message, setMessage] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null)

  const all = profiles.data ?? []
  const conditions = reference.data?.conditions ?? []
  // A new choice only makes sense on a temperature that would be offered.
  const choosable = conditions.filter(c => c.persistent && c.active)
  const rows = keptAtOptionRows(all, conditions, roots.data ?? [])
  const busy = save.isPending || remove.isPending

  function check(draft: Draft, exceptId?: string): string | null {
    if (!draft.name.trim()) return 'Give the option a name, e.g. "Freezer B".'
    if (!draft.condition) return 'Pick the temperature it is kept at.'
    if (optionNameTaken(all, draft.name, exceptId)) return `There is already an option called "${draft.name.trim()}".`
    return null
  }

  function add() {
    if (!adding) return
    const problem = check(adding)
    if (problem) return setMessage({ kind: 'error', text: problem })
    setMessage(null)
    save.mutate(
      {
        payload: {
          profileType: 'storage',
          name: adding.name.trim(),
          fields: keptAtOptionFields(undefined, adding.condition),
        },
      },
      {
        onSuccess: p => {
          setAdding(null)
          setMessage({ kind: 'ok', text: `Added "${p.name}"` })
        },
        onError: err => setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Could not add.' }),
      },
    )
  }

  function saveEdit(row: KeptAtOptionRow) {
    if (!editing) return
    const problem = check(editing.draft, row.profile.id)
    if (problem) return setMessage({ kind: 'error', text: problem })
    setMessage(null)
    save.mutate(
      {
        id: row.profile.id,
        payload: {
          name: editing.draft.name.trim(),
          fields: keptAtOptionFields(row.profile.fields, editing.draft.condition),
        },
      },
      {
        onSuccess: p => {
          refreshStorage()
          setEditing(null)
          setMessage({ kind: 'ok', text: `Saved "${p.name}"` })
        },
        onError: err => setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Could not save.' }),
      },
    )
  }

  function confirmDelete() {
    const row = deleting
    if (!row) return
    setMessage(null)
    remove.mutate(row.profile.id, {
      onSuccess: () => {
        refreshStorage()
        setMessage({ kind: 'ok', text: `Deleted "${row.profile.name}"` })
      },
      onError: err => setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Could not delete.' }),
      onSettled: () => setDeleting(null),
    })
  }

  const loading = profiles.isLoading || reference.isLoading
  const loadError = profiles.error ?? reference.error

  return (
    <div className='flex min-h-0 flex-col gap-3'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <p className='max-w-2xl text-sm text-muted-foreground'>
          The choices a unit's Kept at menu offers. Each is a name for one temperature; a temperature needs at
          least one before a unit can be kept at it.
        </p>
        {canEdit && !adding && (
          <div className='flex flex-col items-end gap-1'>
            <Button
              size='sm'
              disabled={busy || choosable.length === 0}
              onClick={() => {
                setMessage(null)
                setEditing(null)
                setAdding({ name: '', condition: '' })
              }}
            >
              ＋ Add option
            </Button>
            {!loading && choosable.length === 0 && (
              <p className='max-w-xs text-right text-xs text-muted-foreground'>
                No temperature is both for storage and active. Switch one on in the Temperatures tab first.
              </p>
            )}
          </div>
        )}
      </div>

      <div className='min-h-0 flex-1 overflow-y-auto rounded-md border'>
        {loading ? (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        ) : loadError ? (
          <p className='p-4 text-sm text-destructive'>
            Could not load Kept at options: {loadError instanceof Error ? loadError.message : 'unknown error'}
          </p>
        ) : (
          <Table>
            <TableHeader className='sticky top-0 z-10'>
              <TableRow>
                <TableHead className='w-[30%]'>Name</TableHead>
                <TableHead className='w-[26%]'>Temperature</TableHead>
                <TableHead>Usable</TableHead>
                <TableHead className='w-28 text-right'>Units using it</TableHead>
                <TableHead className='w-20' />
              </TableRow>
            </TableHeader>
            <TableBody>
              {adding && (
                <TableRow className='bg-gray-50/60 hover:bg-gray-50/60'>
                  <TableCell className='align-top'>
                    <Input
                      autoFocus
                      value={adding.name}
                      disabled={busy}
                      onChange={e => {
                        setMessage(null)
                        setAdding(a => a && { ...a, name: e.target.value })
                      }}
                      onKeyDown={e => e.key === 'Enter' && add()}
                      placeholder='e.g. Freezer B'
                      aria-label='Name of the new option'
                      className='h-8 bg-white text-sm'
                    />
                  </TableCell>
                  <TableCell className='align-top'>
                    <TemperatureSelect
                      value={adding.condition}
                      conditions={choosable}
                      disabled={busy}
                      onChange={condition => setAdding(a => a && { ...a, condition })}
                      label='Temperature of the new option'
                    />
                  </TableCell>
                  <TableCell colSpan={3} className='align-top'>
                    <div className='flex justify-end gap-2'>
                      <Button size='sm' variant='outline' className='h-8' disabled={busy} onClick={() => setAdding(null)}>
                        Cancel
                      </Button>
                      <Button size='sm' className='h-8' disabled={busy} onClick={add}>
                        {save.isPending ? 'Adding…' : 'Add'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {rows.map(row => {
                const draft = editing?.id === row.profile.id ? editing.draft : null
                return (
                  <TableRow key={row.profile.id} className={cn(!row.usable && 'text-muted-foreground')}>
                    <TableCell className='align-top'>
                      {draft ? (
                        <Input
                          autoFocus
                          value={draft.name}
                          disabled={busy}
                          onChange={e => {
                            setMessage(null)
                            setEditing({ id: row.profile.id, draft: { ...draft, name: e.target.value } })
                          }}
                          onKeyDown={e => e.key === 'Enter' && saveEdit(row)}
                          aria-label={`Name of ${row.profile.name}`}
                          className='h-8 text-sm'
                        />
                      ) : (
                        <span className='text-sm font-medium'>{profileDisplayName(row.profile.name)}</span>
                      )}
                    </TableCell>
                    <TableCell className='align-top'>
                      {draft ? (
                        <>
                          <TemperatureSelect
                            value={draft.condition}
                            // Its current temperature stays pickable even when it is no longer offered.
                            conditions={withCurrent(choosable, row.condition)}
                            disabled={busy}
                            onChange={condition => setEditing({ id: row.profile.id, draft: { ...draft, condition } })}
                            label={`Temperature of ${row.profile.name}`}
                          />
                          {row.units > 0 && draft.condition !== row.condition?.name && (
                            <p className='mt-1 text-xs text-amber-700'>
                              Also changes it for the {row.units} unit{row.units === 1 ? '' : 's'} kept at this.
                            </p>
                          )}
                        </>
                      ) : (
                        <KeptAtBadge condition={row.condition?.name ?? null} />
                      )}
                    </TableCell>
                    <TableCell className='align-top text-sm'>
                      {row.usable ? 'Yes' : <span className='text-xs'>{row.reason}</span>}
                    </TableCell>
                    <TableCell className='align-top text-right text-sm tabular-nums'>{row.units}</TableCell>
                    <TableCell className='align-top'>
                      {canEdit &&
                        (draft ? (
                          <div className='flex justify-end gap-1'>
                            <Button size='sm' variant='outline' className='h-8 px-2' disabled={busy} onClick={() => setEditing(null)}>
                              Cancel
                            </Button>
                            <Button size='sm' className='h-8 px-2' disabled={busy} onClick={() => saveEdit(row)}>
                              {save.isPending ? 'Saving…' : 'Save'}
                            </Button>
                          </div>
                        ) : (
                          <div className='flex justify-end gap-2 pt-1'>
                            <button
                              type='button'
                              className='text-gray-400 hover:text-primary disabled:opacity-40'
                              title='Edit option'
                              aria-label={`Edit ${row.profile.name}`}
                              disabled={busy}
                              onClick={() => {
                                setMessage(null)
                                setAdding(null)
                                setEditing({
                                  id: row.profile.id,
                                  draft: { name: row.profile.name, condition: row.condition?.name ?? '' },
                                })
                              }}
                            >
                              <Pencil className='h-4 w-4' />
                            </button>
                            <button
                              type='button'
                              className='text-gray-300 hover:text-red-500 disabled:opacity-40'
                              title='Delete option'
                              aria-label={`Delete ${row.profile.name}`}
                              disabled={busy}
                              onClick={() => setDeleting(row)}
                            >
                              <Trash2 className='h-4 w-4' />
                            </button>
                          </div>
                        ))}
                    </TableCell>
                  </TableRow>
                )
              })}

              {rows.length === 0 && !adding && (
                <TableRow>
                  <TableCell colSpan={5} className='py-10 text-center text-sm text-muted-foreground'>
                    No Kept at options yet. Add one with ＋ Add option.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {message && (
        <p role='status' className={cn('text-sm', message.kind === 'error' ? 'text-destructive' : 'text-green-700')}>
          {message.text}
        </p>
      )}

      <ConfirmDeleteDialog
        name={deleting?.profile.name ?? ''}
        open={deleting !== null}
        pending={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={confirmDelete}
      >
        {deleting && (
          <ul className='list-disc space-y-1 pl-5'>
            <li>
              {deleting.units > 0
                ? `${deleting.units} storage unit${deleting.units === 1 ? ' is' : 's are'} kept at this option. `
                : ''}
              Every unit, rack or box kept at it loses that temperature and takes the one set on the level
              above, if any. A unit has nothing above it, so it becomes Not set.
            </li>
            <li>
              Materials on this option keep only a temperature set on the material itself; one without its
              own stops requiring any temperature.
            </li>
          </ul>
        )}
      </ConfirmDeleteDialog>
    </div>
  )
}

/** `choosable`, plus the temperature an option already points at if it is not among them. */
function withCurrent(choosable: StorageConditionMeta[], current: StorageConditionMeta | null): StorageConditionMeta[] {
  return current && !choosable.some(c => c.name === current.name) ? [...choosable, current] : choosable
}

function TemperatureSelect({
  value,
  conditions,
  disabled,
  onChange,
  label,
}: {
  value: string
  conditions: StorageConditionMeta[]
  disabled: boolean
  onChange: (condition: string) => void
  label: string
}) {
  return (
    <Select value={value || undefined} disabled={disabled} onValueChange={onChange}>
      <SelectTrigger className='h-8 bg-white text-sm' aria-label={label}>
        <SelectValue placeholder='Pick a temperature' />
      </SelectTrigger>
      <SelectContent>
        {conditions.map(c => (
          <SelectItem key={c.name} value={c.name}>
            {c.temperature ? `${c.temperature} · ${profileDisplayName(c.label)}` : c.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
